<script setup lang="ts">
import { computed, ref } from "vue";
import ProgressBar from "primevue/progressbar";
import { PresetHelper } from "@/helpers/preset.helper";

export interface ProgressBarComponentProps {
  value?: number;
  timeToValue?: number; // Time in seconds
  mode?: string;
}

const props = withDefaults(defineProps<ProgressBarComponentProps>(), {
  value: 0,
  timeToValue: 30,
  mode: "determinate"
});

const progress = ref(0);
const mode = ref(props.mode);

const updateProgress = () => {
  // Increment by 1/10 of the target value
  progress.value += props.value / 10;

  if (progress.value < props.value) {
    setTimeout(updateProgress, props.timeToValue * 100);
  } else if (props.value === progress.value) {
    mode.value = "indeterminate";
  }
};

// Begin incrementing progress or display static value
if (props.timeToValue > 0) {
  updateProgress();
} else {
  progress.value = props.value;
}

const presetRef = computed(
  () =>
    new PresetHelper({
      classMap: {
        tai_progressbar_root: {
          position: ["relative overflow-hidden"],
          colors: ["bg-system-bg border border-system-light-grey"],
          shape: ["h-4 rounded-xl"]
        },
        tai_progressbar_value: {
          colors: ["bg-contrast-bitter-sweet"],
          shape: ["h-full rounded-xl"],
          transition: ["transition-width duration-1000 ease-in-out"]
        },
        "tai_progressbar_value--indeterminate": {
          animation: ["animate-pulse"]
        },
        tai_progressbar_label: ["hidden"]
      },
      preset: {
        root: () => ["tai_progressbar_root"],
        label: () => ["tai_progressbar_label"],
        value: () => [
          "tai_progressbar_value",
          {
            "tai_progressbar_value--indeterminate":
              mode.value === "indeterminate"
          }
        ]
      }
    })
);
</script>

<template>
  <ProgressBar
    :value="progress"
    unstyled
    :pt="presetRef.preset"
    data-testid="tai-progressbar"
  />
</template>
